import { SubmissionAssignSuggestions } from './types';
import { topicsMatchFunction, classicAssignmentSuggestion } from './algorithm';
import { fairBidSatisfaction } from './fair-bid';

// ============================================================================
// Biblioteca de algoritmos de alocação (Strategy pattern — objetos puros).
// ============================================================================
// Cada algoritmo é um objeto que implementa `AssignmentAlgorithm`: metadados
// (id/rótulo), a especificação dos seus parâmetros e a função `run`. O componente
// despacha por `algorithmIndex` → `ALGORITHMS[index].run(...)`. Adicionar um
// algoritmo = criar um objeto novo e incluí-lo em `ALGORITHMS` (nenhuma mudança
// no fluxo do componente). Ver docs/eixo de biblioteca de algoritmos.

/**
 * Um parâmetro de entrada de um algoritmo (ex.: fator de incremento/decremento).
 * Metadados usados p/ (a) ler o valor do reactive form pelo `name` e (b) —
 * futuramente — renderizar os inputs de forma data-driven. Por ora os inputs da
 * aba seguem explícitos no template (decisão híbrida: registro p/ a LÓGICA, UI
 * explícita enquanto há poucos parâmetros).
 */
export interface AlgorithmParamSpec {
  /** nome do FormControl em `formSelectionFields`. */
  name: string;
  /** chave i18n do rótulo do campo. */
  labelKey: string;
  /** valor inicial. */
  default: number;
  min?: number;
}

/**
 * Dados compartilhados por QUALQUER algoritmo (independem de qual foi escolhido):
 * derivam dos comitês e da configuração de revisão do evento.
 */
export interface AlgorithmContext {
  numberOfCommittees: number;
  numberOfReviewsRequired: number;
}

/**
 * Contrato de um algoritmo de alocação. `run` recebe a matriz GENÉRICA (submissões
 * × comitês, com elegibilidade já resolvida e SEM pesos), o contexto e os valores
 * dos parâmetros; devolve a matriz com `possiblesAssign[].weight` calculado e
 * `suggestedAssign` preenchido. A montagem genérica da matriz e o consumo da saída
 * (tabelas/precompute) ficam no componente — são iguais p/ todo algoritmo.
 */
export interface AssignmentAlgorithm {
  /** identificador estável (ex.: 'highest-match-first'). */
  id: string;
  /** chave i18n do rótulo da aba. */
  labelKey: string;
  /** chave i18n da explicação amigável (não-técnica) exibida ao TPC Chair ao
   *  escolher o algoritmo no dropdown. */
  descriptionKey: string;
  params: AlgorithmParamSpec[];
  run(
    matrix: SubmissionAssignSuggestions[],
    ctx: AlgorithmContext,
    params: Record<string, number>
  ): SubmissionAssignSuggestions[];
}

/**
 * "Highest match first" — o algoritmo clássico (como no JEMS1): peso por afinidade
 * de tópicos (fatores +/−) e atribuição em 4 fases (bid×2, interesse×2). Empacota
 * `topicsMatchFunction` (peso) + `classicAssignmentSuggestion` (fases).
 */
export const highestMatchFirst: AssignmentAlgorithm = {
  id: 'highest-match-first',
  labelKey: 'admin.event.reviews.assign-suggestion.algorithms.highest-match-first',
  descriptionKey: 'admin.event.reviews.assign-suggestion.algorithm-descriptions.highest-match-first',
  params: [
    { name: 'positiveMatchWeight', labelKey: 'admin.event.reviews.assign-suggestion.positive-match-weight', default: 1 },
    { name: 'negativeMatchWeight', labelKey: 'admin.event.reviews.assign-suggestion.negative-match-weight', default: 1 },
  ],
  run(matrix, ctx, params) {
    // Peso: afinidade de tópicos com os fatores de incremento/decremento.
    matrix.forEach(sub => sub.possiblesAssign.forEach(cell => {
      cell.weight = topicsMatchFunction(
        cell.userPreferences, cell.submission,
        params.positiveMatchWeight, params.negativeMatchWeight);
    }));
    // Atribuição: fases claim/interesse (mutam suggestedAssign; retorna ordenada).
    return classicAssignmentSuggestion(matrix, ctx.numberOfCommittees, ctx.numberOfReviewsRequired);
  },
};

/**
 * Registro (biblioteca) de algoritmos. A ordem casa com as abas: a aba N usa
 * `ALGORITHMS[N]`. Por ora só um; novos algoritmos entram aqui.
 */
export const ALGORITHMS: AssignmentAlgorithm[] = [
  highestMatchFirst,
  fairBidSatisfaction,
];
